import { Icon } from "./Icon";
import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { fetchAssignedWorkoutTree } from "../lib/assignedWorkoutTree";
import { wireNumber } from "../lib/wireNumber";
import {
  describeSetSave, draftFromStored, draftNewRow, editPlan,
  type SetDraftRow, type SetStored,
} from "../lib/setEdit";
import { AddSetSplit } from "./AddSetSplit";

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * W22(b) — THE PER-EXERCISE EDITOR, OPENED FROM A TILE ON THE CALENDAR.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `EVERFIT-MAP` §2b-ter, read live: clicking an exercise tile on a day card opens a FLOATING
 * editor — name, a `Set · Weight · Reps · Rest` table, Save — and this is the shape W17d was
 * reaching for: one exercise edited from the calendar without opening the whole workout.
 *
 * 🔴 **IT WRITES TO A CLIENT'S TRAINING, AND THE WRITE WAS REHEARSED PER IDENTITY, ROLLED BACK,
 * BEFORE THIS FILE EXISTED** — `~/ht-logs/day-0919/web-dig4/w22b/rehearse-set-edit.sql`, six
 * verdicts on a REAL assigned copy: the owning coach updates 1 row; the CLIENT updates 0 and is
 * refused 42501 on an INSERT; another real coach updates 0; anon is refused at the GRANT; and the
 * value the coach wrote is still there afterwards, so none of the refused arms wrote anything.
 * Two broken controls abort at their own arm. **No policy changed: `coach_workout_sets_owner_all`
 * already grants the workout's owner ALL, and an assigned copy is a workout the coach owns.**
 *
 * 🔴 **W22(b2), 2026-09-19 — IT NOW ADDS AND REMOVES SETS, AND CARRIES A TIMED SET'S DURATION.**
 * The first cut filed all three because each is a different write. They were rehearsed the same
 * way the UPDATE was, before this component grew a control for them:
 * `~/ht-logs/day-0919/web-dig5/w22b2/rehearse-add-remove.sql`, TEN verdicts on a REAL assigned
 * copy — the owning coach INSERTs 1 and DELETEs 1, `time_seconds` round-trips and updates, the
 * CLIENT's insert is refused 42501 and its delete matches 0 rows, another real coach deletes 0,
 * anon is refused at the GRANT, and the exercise holds exactly what it held at V0 afterwards.
 * A BROKEN CONTROL (V4 run as the coach while claiming to be the client) aborts at V4, so the
 * rehearsal can fail. **No policy changed: `coach_workout_sets_owner_all` already grants ALL.**
 *
 * ⚠️ **THE DURATION'S COLUMN IS `time_seconds`, MEASURED, NOT `per_set_seconds`.** The latter is
 * the PHONE's column on `program_session_exercises`, a different table for a different half of
 * the product; naming it here would have been the vocabulary mismatch this repo is named after.
 * Without the box, a hold prescribed as `1 × 1:00` opened in an editor that could not show the
 * minute — and a coach who then pressed Save on any other field left it untouched only by luck.
 *
 * ⚠️ **THE WINDOW RE-READS THE TREE RATHER THAN BEING HANDED ONE.** The tile that opened it holds
 * a PREVIEW — name and a dose SENTENCE — and a sentence is not a set table. Reading the door
 * again costs one round trip and means the boxes hold what the database holds, not what the
 * calendar last painted.
 */
export function ExerciseSetWindow({ workoutId, exerciseRowId, onClose, onSaved }: {
  workoutId: string;
  exerciseRowId: string;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "error"; message: string }
    | { kind: "ready"; name: string; before: SetStored[] }
  >({ kind: "loading" });
  const [rows, setRows] = useState<SetDraftRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    (async () => {
      const r = await fetchAssignedWorkoutTree(
        supabase.rpc.bind(supabase) as Parameters<typeof fetchAssignedWorkoutTree>[0], workoutId);
      if (cancelled) return;
      if (r.tree === null) { setState({ kind: "error", message: r.error }); return; }
      for (const sec of r.tree.coach_workout_sections ?? []) {
        for (const ex of sec.coach_workout_exercises ?? []) {
          if (ex.id !== exerciseRowId) continue;
          /* ⚠️ `weight_kg` ARRIVES AS A NUMBER OR A STRING depending on which serialiser answered
             (`numeric` over PostgREST is a string, over this RPC's `jsonb_build_object` it is a
             number). `wireNumber` reads both and is idempotent — the same narrowing
             `workoutPreview` makes, in the one place it is made. */
          const before: SetStored[] = (ex.coach_workout_sets ?? [])
            .slice()
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .map((s) => ({
              id: s.id, reps: s.reps,
              weight_kg: wireNumber(s.weight_kg), rest_seconds: s.rest_seconds,
              time_seconds: s.time_seconds ?? null,
              set_type: s.set_type ?? "regular",
              position: s.position ?? 0,
            }));
          setState({ kind: "ready", name: ex.coach_exercises?.name ?? "Untitled exercise", before });
          setRows(before.map(draftFromStored));
          return;
        }
      }
      /* A row the door does not carry is a DIFFERENT state from a failed read: the exercise was
         removed, or this window was opened on a stale tile. Saying "could not load" would send a
         coach looking for a network problem. */
      setState({ kind: "error", message: "That exercise is no longer in this workout." });
    })();
    return () => { cancelled = true; };
  }, [workoutId, exerciseRowId, reload]);

  /* `showModal()` on an already-open dialog THROWS, so it is guarded rather than assumed — the
     same note `WorkoutOverviewWindow` carries, for the same browser behaviour. */
  useEffect(() => {
    const el = dialogRef.current;
    if (el !== null && !el.open) el.showModal();
    heading.current?.focus();
  }, [state.kind]);

  async function save() {
    if (state.kind !== "ready" || busy) return;
    const plan = editPlan(state.before, rows);
    if (plan.kind === "refused") { setSaid(plan.message); return; }
    /* 🔴 A NO-OP SAVE WRITES NOTHING AND SAYS SO. Sending every row on every press means a coach
       who opened the window and closed it has written to their client's training. */
    if (plan.kind === "nothing") { setSaid("Nothing changed."); return; }
    setBusy(true);
    let failure: { message: string } | null = null;
    for (const p of plan.patches) {
      const { id, ...fields } = p;
      const { error } = await supabase.from("coach_workout_sets").update(fields).eq("id", id);
      /* ⚠️ THE FIRST REFUSAL STOPS THE LOOP, and the sentence names how many landed BEFORE it.
         Carrying on would leave a coach with a half-written exercise and a message about the
         last row only. */
      if (error) { failure = error; break; }
    }
    /* 🔴 THE ORDER IS UPDATE → INSERT → DELETE, AND IT IS THE ORDER THAT LOSES LEAST ON A
       REFUSAL. A delete last means a failure anywhere earlier leaves every row the coach still
       wanted standing; deleting first would destroy rows and then fail to write the ones that
       were meant to replace them. */
    if (failure === null && plan.inserts.length > 0) {
      const { error } = await supabase.from("coach_workout_sets")
        .insert(plan.inserts.map((i) => ({ ...i, workout_exercise_id: exerciseRowId })));
      if (error) failure = error;
    }
    if (failure === null && plan.deletes.length > 0) {
      const { error } = await supabase.from("coach_workout_sets").delete().in("id", plan.deletes);
      if (error) failure = error;
    }
    setBusy(false);
    setSaid(describeSetSave(
      failure === null
        ? { saved: plan.patches.length, added: plan.inserts.length, removed: plan.deletes.length }
        : { saved: 0, added: 0, removed: 0 },
      failure,
    ));
    /* ⚠️ THE WINDOW RE-READS ITSELF AFTER A WRITE THAT CHANGED THE ROW LIST. The drafts hold
       local keys for rows the database has just given real ids to, and a second Save would then
       insert them all over again. `reload` re-runs the door. */
    if (failure === null) {
      onSaved?.();
      if (plan.inserts.length > 0 || plan.deletes.length > 0) setReload((n) => n + 1);
    }
  }

  return (
    <dialog ref={dialogRef} className="ht-window setwindow"
            onClose={onClose}
            aria-labelledby="setwindow-title"
            onCancel={(e) => { e.preventDefault(); onClose(); }}>
      {/* 🔴 `.ht-window-frame` IS NOT DECORATION AND THE STILL IS WHAT SAID SO. `.ht-window[open]`
          is `display: flex` — a ROW — so a dialog whose children are the head, the table and the
          foot lays them out SIDE BY SIDE, with the exercise's name wrapped one letter per line.
          The frame is the column, and it is what carries the surface, the hairline and the radius
          (the dialog element is only a box, by that rule's own comment). */}
      <div className="ht-window-frame">
      <div className="ht-window-head">
        <h2 className="ht-title setwindow-title" id="setwindow-title" tabIndex={-1} ref={heading}>
          {state.kind === "ready" ? state.name : "Exercise"}
        </h2>
        <button type="button" className="ht-button ht-button-quiet" onClick={onClose}>Close</button>
      </div>

      {state.kind === "loading" && <p className="ht-callout" aria-live="polite">Loading…</p>}
      {state.kind === "error" && <p className="ht-callout" role="alert">{state.message}</p>}

      {state.kind === "ready" && (
        <>
          {rows.length === 0 ? (
            /* A real state, and a different one from a failed read: the exercise is on the day
               with no sets on it, which the builder also calls a problem worth naming.
               ⚠️ IT ASKS THE DRAFT, NOT THE DOOR. Since W22(b2) a coach can remove every row,
               and `state.before.length` would keep drawing an empty table over no rows. */
            <p className="ht-callout">This exercise has no sets yet.</p>
          ) : (
            <table className="setwindow-table">
              <thead>
                <tr>
                  <th scope="col" className="ht-overline">Set</th>
                  <th scope="col" className="ht-overline">Weight</th>
                  <th scope="col" className="ht-overline">Reps</th>
                  {/* 🔴 W22(b2) — THE COLUMN THE TABLE HAD NO BOX FOR. A hold prescribed as
                      `1 × 1:00` opened in an editor that could not show the minute, and a Save
                      on any other field left it untouched only because `editPlan` sends only
                      what moved. The column is `coach_workout_sets.time_seconds`. */}
                  <th scope="col" className="ht-overline">Time</th>
                  <th scope="col" className="ht-overline">Rest</th>
                  {/* The trash column's head is empty to the eye and named to a screen reader —
                      a `<th>` with nothing in it leaves the last column unlabelled in the table
                      a screen reader builds. */}
                  <th scope="col"><span className="ht-visually-hidden">Remove</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.key}>
                    <th scope="row" className="ht-data-small">{i + 1}</th>
                    {/* ⚠️ EVERY BOX CARRIES ITS OWN LABEL, clipped. A table header is not an
                        accessible name for an input inside the cell below it in every screen
                        reader, and four unnamed boxes per row is what that costs. */}
                    <td>
                      <label>
                        <span className="ht-visually-hidden">Weight for set {i + 1}</span>
                        <input className="ht-input setwindow-box" inputMode="decimal" value={r.weightKg}
                               onChange={(e) => { const v = e.target.value; setRows((rs) => rs.map((x) => x.key === r.key ? { ...x, weightKg: v } : x)); }} />
                      </label>
                    </td>
                    <td>
                      <label>
                        <span className="ht-visually-hidden">Reps for set {i + 1}</span>
                        <input className="ht-input setwindow-box" inputMode="numeric" value={r.reps}
                               onChange={(e) => { const v = e.target.value; setRows((rs) => rs.map((x) => x.key === r.key ? { ...x, reps: v } : x)); }} />
                      </label>
                    </td>
                    <td>
                      <label>
                        <span className="ht-visually-hidden">Time in seconds for set {i + 1}</span>
                        <input className="ht-input setwindow-box" inputMode="numeric" value={r.timeSeconds}
                               onChange={(e) => { const v = e.target.value; setRows((rs) => rs.map((x) => x.key === r.key ? { ...x, timeSeconds: v } : x)); }} />
                      </label>
                    </td>
                    <td>
                      <label>
                        <span className="ht-visually-hidden">Rest in seconds for set {i + 1}</span>
                        <input className="ht-input setwindow-box" inputMode="numeric" value={r.restSeconds}
                               onChange={(e) => { const v = e.target.value; setRows((rs) => rs.map((x) => x.key === r.key ? { ...x, restSeconds: v } : x)); }} />
                      </label>
                    </td>
                    {/* 🔴 REMOVE TAKES THE ROW OUT OF THE DRAFT AND DESTROYS NOTHING UNTIL SAVE.
                        A trash that deleted on the click would be an irreversible write behind a
                        single tap, on a day a client may already have trained; here the removal
                        is a change like any other, and the save's own sentence NAMES it rather
                        than folding it into a total. */}
                    <td>
                      <button type="button" className="setwindow-remove"
                              onClick={() => { setRows((rs) => rs.filter((x) => x.key !== r.key)); }}>
                        {/* W72 step 7 (Proposal 7 rule 9): a 1.5 px LINE icon, the rail's set — never an emoji in the chrome. */}
                        <Icon name="trash" className="setwindow-remove-glyph" />
                        <span className="ht-visually-hidden">Remove set {i + 1}</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* 🔴 W22(b2) — THE SAME SPLIT CONTROL THE BUILDER USES, his ask with their screenshots.
              One component, so the two surfaces cannot drift into two grammars for one act.
              ⚠️ A NEW ROW COPIES THE LAST ONE, `set_type` INCLUDED — an exercise whose rows are
              all warm-ups would otherwise gain a `regular` row the coach never chose, and this
              editor has no control for that column. */}
          <div className="setwindow-add">
            <AddSetSplit onAdd={(n) => {
              setRows((rs) => {
                const out = [...rs];
                for (let k = 0; k < n; k += 1) {
                  out.push(draftNewRow(out[out.length - 1], `new-${String(Date.now())}-${String(k)}`));
                }
                return out;
              });
            }} />
          </div>

          <div className="ht-window-foot">
            {/* The answer is a live region because the window stays open after a save — a coach
                who pressed Save and saw nothing move would press it again. */}
            {said !== null && <p className="ht-callout setwindow-said" role="status">{said}</p>}
            <button type="button" className="ht-button ht-button-primary"
                    disabled={busy}
                    onClick={() => { void save(); }}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </>
      )}
      </div>
    </dialog>
  );
}
